(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const text = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();

  const parseFramework = (template) => {
    const groups = [];
    let current = null;

    const ensureGroup = (title = '') => {
      if (!current || title) {
        current = { title, rows: [] };
        groups.push(current);
      }
      return current;
    };

    Array.from(template?.content?.children || []).forEach((node) => {
      const tag = node.tagName?.toLowerCase();
      if (tag === 'ul' || tag === 'ol') {
        Array.from(node.querySelectorAll(':scope > li')).forEach((li) => {
          const label = text(li);
          if (label) ensureGroup().rows.push(label);
        });
        return;
      }

      if (tag === 'li') {
        const label = text(node);
        if (label) ensureGroup().rows.push(label);
        return;
      }

      const title = text(node);
      if (title) ensureGroup(title);
    });

    return groups.filter((group) => group.title || group.rows.length);
  };

  const parseSpecValues = (template) => {
    const values = new Map();
    const parseLine = (raw) => {
      const line = String(raw || '').trim();
      if (!line || !line.includes('|')) return;
      const [label, ...valueParts] = line.split('|');
      const cleanLabel = label.trim();
      if (!cleanLabel) return;
      values.set(cleanLabel, valueParts.join('|').trim());
    };

    Array.from(template?.content?.querySelectorAll('li, p') || []).forEach((node) => {
      parseLine(text(node));
    });

    return values;
  };

  const getNavOffset = () => {
    const styles = window.getComputedStyle(document.documentElement);
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
    const headerOffset = Number.parseFloat(styles.getPropertyValue('--header-offset')) || 0;
    const announcementHeight = Number.parseFloat(styles.getPropertyValue('--announcement-height')) || 0;
    return Math.round(headerHeight + headerOffset + announcementHeight || 0);
  };

  const initCompare = (root) => {
    if (root.dataset.seriesCompareReady === 'true') return;
    root.dataset.seriesCompareReady = 'true';

    const productArea = root.querySelector('[data-series-compare-product-area]');
    const specsTarget = root.querySelector('[data-series-compare-specs]');
    const sticky = root.querySelector('[data-series-compare-sticky]');
    const framework = parseFramework(root.querySelector('[data-series-compare-framework]'));
    const sourceMap = new Map();
    const sources = [];
    const selectedSourceKeys = [];
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    let stickyTicking = false;
    let stickyObserver = null;
    let stickySentinel = null;

    const stickyOwner = root.id || `series-compare-${Math.random().toString(36).slice(2)}`;
    if (!root.id) root.id = stickyOwner;
    if (sticky) {
      document.querySelectorAll(`[data-series-compare-sticky-owner="${stickyOwner}"]`).forEach((item) => {
        if (item !== sticky) item.remove();
      });
      sticky.dataset.seriesCompareStickyOwner = stickyOwner;
      sticky.classList.add('series-compare__sticky--detached');
      document.body.append(sticky);
    }

    const scopedAll = (selector) => [
      ...root.querySelectorAll(selector),
      ...(sticky ? [...sticky.querySelectorAll(selector)] : []),
    ];

    const resetSelectMenuPosition = (wrap) => {
      const menu = wrap?.querySelector('[data-series-compare-select-menu]');
      if (!wrap || !menu) return;

      wrap.removeAttribute('data-menu-align');
      menu.style.removeProperty('left');
      menu.style.removeProperty('right');
    };

    const positionSelectMenu = (wrap) => {
      const menu = wrap?.querySelector('[data-series-compare-select-menu]');
      if (!wrap || !menu || !wrap.classList.contains('is-open')) return;

      resetSelectMenuPosition(wrap);
      wrap.setAttribute('data-menu-align', 'left');
      const viewportLeft = window.visualViewport?.offsetLeft || 0;
      const viewportWidth = window.visualViewport?.width || window.innerWidth;
      const viewportRight = viewportLeft + viewportWidth;
      const gutter = 16;
      const wrapRect = wrap.getBoundingClientRect();
      const menuWidth = menu.offsetWidth;
      const leftAlignedRight = wrapRect.left + menuWidth;

      if (leftAlignedRight > viewportRight - gutter) {
        wrap.setAttribute('data-menu-align', 'right');
        return;
      }

      wrap.setAttribute('data-menu-align', 'left');
    };

    const positionOpenMenus = () => {
      scopedAll('[data-series-compare-select-wrap].is-open').forEach(positionSelectMenu);
    };

    const syncStickyVars = () => {
      if (!sticky) return;
      const styles = window.getComputedStyle(root);
      [
        '--series-compare-sticky-top',
        '--series-compare-sticky-bg',
        '--series-compare-line-color',
        '--series-compare-select-bg',
        '--series-compare-select-color',
        '--series-compare-text-color',
        '--series-compare-buy-bg',
        '--series-compare-buy-color',
        '--series-compare-learn-color',
      ].forEach((name) => {
        const value = styles.getPropertyValue(name);
        if (value) sticky.style.setProperty(name, value.trim());
      });
    };

    root.querySelectorAll('[data-series-compare-source]').forEach((source) => {
      const key = source.dataset.sourceKey || source.dataset.productHandle;
      const handle = source.dataset.productHandle;
      if (!key || sourceMap.has(key)) return;
      const sourceData = {
        key,
        handle,
        title: source.dataset.productTitle || '',
        buyUrl: source.dataset.buyUrl || '#',
        learnUrl: source.dataset.learnUrl || '#',
        cardTemplate: source.querySelector('[data-series-compare-card-template]'),
        specs: parseSpecValues(source.querySelector('[data-series-compare-spec-template]')),
      };
      sourceMap.set(key, sourceData);
      sources.push(sourceData);
    });

    const getSource = (key) => {
      if (!key) return null;
      return sourceMap.get(key) || sources.find((source) => source.handle === key) || null;
    };

    root.querySelectorAll('[data-series-compare-slot]').forEach((slot) => {
      const index = Number(slot.dataset.slotIndex || 0);
      selectedSourceKeys[index] = slot.dataset.selectedSourceKey || '';
    });

    const closeAllMenus = () => {
      scopedAll('[data-series-compare-select-wrap].is-open').forEach((wrap) => {
        wrap.classList.remove('is-open');
        resetSelectMenuPosition(wrap);
        wrap.querySelector('[data-series-compare-select-button]')?.setAttribute('aria-expanded', 'false');
      });
    };

    const updateSelectors = (slotIndex, source) => {
      scopedAll(`[data-series-compare-select-wrap][data-slot-index="${slotIndex}"]`).forEach((wrap) => {
        const buttonText = wrap.querySelector('[data-series-compare-select-text]');
        if (buttonText) buttonText.textContent = source?.title || buttonText.dataset.defaultText || 'Select product';
        wrap.querySelectorAll('[data-series-compare-select-option]').forEach((option) => {
          const active = (option.dataset.sourceKey || option.dataset.productHandle) === source?.key;
          option.classList.toggle('is-active', active);
          option.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      });
    };

    const renderSlot = (slotIndex) => {
      const source = getSource(selectedSourceKeys[slotIndex]);
      if (!source) {
        selectedSourceKeys[slotIndex] = '';
        updateSelectors(slotIndex, null);

        root.querySelectorAll(`[data-series-compare-slot][data-slot-index="${slotIndex}"]`).forEach((slot) => {
          const target = slot.querySelector('[data-series-compare-card-target]');
          if (target) target.innerHTML = '';
        });

        scopedAll(`[data-series-compare-sticky-slot][data-slot-index="${slotIndex}"]`).forEach((slot) => {
          const buyLink = slot.querySelector('[data-series-compare-buy-link]');
          const learnLink = slot.querySelector('[data-series-compare-learn-link]');
          if (buyLink) buyLink.href = '#';
          if (learnLink) learnLink.href = '#';
        });
        return;
      }
      selectedSourceKeys[slotIndex] = source.key;
      updateSelectors(slotIndex, source);

      root.querySelectorAll(`[data-series-compare-slot][data-slot-index="${slotIndex}"]`).forEach((slot) => {
        const target = slot.querySelector('[data-series-compare-card-target]');
        if (target && source.cardTemplate) {
          target.innerHTML = '';
          target.append(source.cardTemplate.content.cloneNode(true));
        }
      });

      scopedAll(`[data-series-compare-sticky-slot][data-slot-index="${slotIndex}"]`).forEach((slot) => {
        const buyLink = slot.querySelector('[data-series-compare-buy-link]');
        const learnLink = slot.querySelector('[data-series-compare-learn-link]');
        if (buyLink) buyLink.href = source.buyUrl || '#';
        if (learnLink) learnLink.href = source.learnUrl || '#';
      });
    };

    const renderSpecs = () => {
      if (!specsTarget) return;
      specsTarget.innerHTML = '';

      framework.forEach((group) => {
        const groupEl = document.createElement('section');
        groupEl.className = 'series-compare__spec-group';

        if (group.title) {
          const heading = document.createElement('h3');
          heading.className = 'series-compare__spec-heading';
          heading.textContent = group.title;
          groupEl.append(heading);
        }

        const line = document.createElement('div');
        line.className = 'series-compare__spec-line';
        line.setAttribute('aria-hidden', 'true');
        groupEl.append(line);

        group.rows.forEach((label) => {
          const row = document.createElement('div');
          row.className = 'series-compare__spec-row';

          const labelEl = document.createElement('p');
          labelEl.className = 'series-compare__spec-label';
          labelEl.textContent = label;
          row.append(labelEl);

          const values = document.createElement('div');
          values.className = 'series-compare__spec-values';

          selectedSourceKeys.forEach((sourceKey, index) => {
            if (index > 2) return;
            const value = document.createElement('p');
            value.className = 'series-compare__spec-value';
            value.textContent = getSource(sourceKey)?.specs.get(label) || '';
            values.append(value);
          });

          row.append(values);
          groupEl.append(row);
        });

        specsTarget.append(groupEl);
      });
    };

    const renderAll = () => {
      for (let index = 0; index < 3; index += 1) renderSlot(index);
      renderSpecs();
      syncStickySentinel();
      requestStickyState();
    };

    const syncStickyOffset = () => {
      root.style.setProperty('--series-compare-sticky-top', '0px');
      syncStickyVars();
    };

    const getStickyTriggerOffset = () => 0;

    const setStickyState = (stuck) => {
      const rootRect = root.getBoundingClientRect();
      const active = Boolean(stuck && rootRect.bottom > getStickyTriggerOffset());

      root.classList.toggle('is-stuck', active);
      sticky?.classList.toggle('is-stuck', active);
      sticky?.setAttribute('aria-hidden', active ? 'false' : 'true');
    };

    const updateStickyState = () => {
      stickyTicking = false;
      if (!stickySentinel) {
        setStickyState(false);
        return;
      }

      const triggerOffset = getStickyTriggerOffset();
      const sentinelRect = stickySentinel.getBoundingClientRect();
      setStickyState(sentinelRect.top <= triggerOffset);
    };

    const observeStickySentinel = () => {
      stickyObserver?.disconnect();
      stickyObserver = null;

      if (!stickySentinel || typeof IntersectionObserver !== 'function') return;

      const triggerOffset = getStickyTriggerOffset();
      stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          setStickyState(!entry.isIntersecting && entry.boundingClientRect.top <= triggerOffset);
        },
        {
          root: null,
          rootMargin: `-${triggerOffset}px 0px 0px 0px`,
          threshold: 0,
        }
      );
      stickyObserver.observe(stickySentinel);
    };

    const syncStickySentinel = () => {
      root.style.setProperty('--series-compare-sticky-top', '0px');

      const trigger = productArea?.querySelector('.series-compare__learn');
      if (!trigger) {
        stickySentinel?.remove();
        stickySentinel = null;
        observeStickySentinel();
        return;
      }

      if (!stickySentinel) {
        stickySentinel = document.createElement('span');
        stickySentinel.className = 'series-compare__sticky-sentinel';
        stickySentinel.setAttribute('aria-hidden', 'true');
      }

      trigger.insertAdjacentElement('afterend', stickySentinel);
      observeStickySentinel();
    };

    const requestStickyState = () => {
      if (stickyTicking) return;
      stickyTicking = true;
      window.requestAnimationFrame(updateStickyState);
    };

    const handleClick = (event) => {
      const selectButton = event.target.closest('[data-series-compare-select-button]');
      if (selectButton && (root.contains(selectButton) || sticky?.contains(selectButton))) {
        event.preventDefault();
        const wrap = selectButton.closest('[data-series-compare-select-wrap]');
        const wasOpen = wrap?.classList.contains('is-open');
        closeAllMenus();
        if (wrap && !wasOpen) {
          wrap.classList.add('is-open');
          selectButton.setAttribute('aria-expanded', 'true');
          positionSelectMenu(wrap);
        }
        return;
      }

      const option = event.target.closest('[data-series-compare-select-option]');
      if (option && (root.contains(option) || sticky?.contains(option))) {
        event.preventDefault();
        const slotIndex = Number(option.dataset.slotIndex || 0);
        selectedSourceKeys[slotIndex] = option.dataset.sourceKey || option.dataset.productHandle || '';
        renderAll();
        closeAllMenus();
      }
    };

    root.addEventListener('click', handleClick);
    sticky?.addEventListener('click', handleClick);

    document.addEventListener('click', (event) => {
      if (root.contains(event.target) || sticky?.contains(event.target)) return;
      closeAllMenus();
    });

    syncStickyOffset();
    renderAll();

    window.addEventListener('scroll', requestStickyState, { passive: true });
    document.addEventListener('scroll', requestStickyState, { passive: true });
    window.visualViewport?.addEventListener('scroll', () => {
      requestStickyState();
      positionOpenMenus();
    }, { passive: true });
    window.addEventListener('resize', () => {
      syncStickyOffset();
      syncStickySentinel();
      requestStickyState();
      positionOpenMenus();
    });
    mobileQuery.addEventListener?.('change', () => {
      syncStickyOffset();
      syncStickySentinel();
      requestStickyState();
      renderSpecs();
      positionOpenMenus();
    });
    document.fonts?.ready?.then(() => {
      syncStickySentinel();
      requestStickyState();
    }).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-series-compare]').forEach(initCompare);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
